import React, { useEffect, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';

export type LomavisFontOption = {
  value: string;
  label: string;
};

export type LomavisFontTypeaheadProps = {
  id: string;
  options: LomavisFontOption[];
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  onPreview?: (font: LomavisFontOption) => void;
  onSelect: (font: LomavisFontOption) => void;
};

/**
 * A controlled, single-select font picker for local font catalogs. It keeps
 * the selected value readable in its own typeface and can lazy-load previews
 * through `onPreview` without changing the selected value.
 */
export const LomavisFontTypeahead: React.FC<LomavisFontTypeaheadProps> = ({
  id,
  options,
  value,
  disabled = false,
  placeholder = 'Search fonts…',
  onFocus,
  onPreview,
  onSelect
}) => {
  const selectedOption = options.find((font) => font.value === value) ?? {
    value,
    label: value
  };
  const [selected, setSelected] = useState<LomavisFontOption[]>([
    selectedOption
  ]);

  useEffect(() => {
    setSelected([
      options.find((font) => font.value === value) ?? {
        value,
        label: value
      }
    ]);
  }, [options, value]);

  return (
    <Typeahead
      id={id}
      labelKey="label"
      options={options}
      selected={selected}
      disabled={disabled}
      minLength={0}
      maxResults={100}
      maxHeight="min(300px, calc(100vh - 1rem))"
      paginate
      flip
      positionFixed
      placeholder={placeholder}
      onFocus={onFocus}
      onInputChange={() => setSelected([])}
      onChange={(nextSelected) => {
        const font = nextSelected[0] as LomavisFontOption | undefined;
        if (!font) return;
        setSelected([font]);
        onSelect(font);
      }}
      renderMenuItemChildren={(option) => {
        const font = option as LomavisFontOption;
        return (
          <span
            style={{ fontFamily: font.value }}
            onMouseEnter={() => onPreview?.(font)}
          >
            {font.label}
          </span>
        );
      }}
      renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
        <input
          {...inputProps}
          ref={(node) => {
            inputRef(node);
            referenceElementRef(node);
          }}
          className={`${inputProps.className ?? ''} form-control`}
          style={{ ...inputProps.style, fontFamily: value }}
        />
      )}
    />
  );
};
